/**
 * Mobile-only bottom banner: "Create Chat PWA" – prompts users to install the chat app on their phone.
 * Shown when on mobile, not already installed (standalone), and not dismissed.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal } from 'antd';
import { MobileOutlined, CloseOutlined } from '@ant-design/icons';
import './MobileChatPwaBanner.css';

const STORAGE_KEY_DISMISSED = 'pwa-chat-banner-dismissed';
const DISMISS_DAYS = 7;

const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    nav.standalone === true ||
    document.referrer.includes('android-app://')
  );
};

const wasDismissedRecently = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DISMISSED);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    if (Number.isNaN(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
};

const setDismissed = () => {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY_DISMISSED, String(until));
  } catch (_) {}
};

export const MobileChatPwaBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [iosInstructionsVisible, setIosInstructionsVisible] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const deferredPromptRef = useRef<{ prompt: () => void; userChoice: Promise<{ outcome: string }> } | null>(null);

  const updateVisibility = useCallback(() => {
    if (!isMobile() || isStandalone() || wasDismissedRecently()) {
      setVisible(false);
      return;
    }
    setIsMobileDevice(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    updateVisibility();

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as unknown as { prompt: () => void; userChoice: Promise<{ outcome: string }> };
      updateVisibility();
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('resize', updateVisibility);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [updateVisibility]);

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') setVisible(false);
      deferredPromptRef.current = null;
      return;
    }
    setIosInstructionsVisible(true);
  };

  const handleDismiss = () => {
    setDismissed();
    setVisible(false);
  };

  if (!visible || !isMobileDevice) return null;

  return (
    <>
      <div className="mobile-chat-pwa-banner">
        <div className="mobile-chat-pwa-banner-inner">
          <MobileOutlined className="mobile-chat-pwa-banner-icon" />
          <span className="mobile-chat-pwa-banner-text">Use Chat in an app</span>
          <Button type="primary" size="small" className="mobile-chat-pwa-banner-btn" onClick={handleInstall}>
            Create Chat PWA
          </Button>
          <button type="button" className="mobile-chat-pwa-banner-close" onClick={handleDismiss} aria-label="Dismiss">
            <CloseOutlined />
          </button>
        </div>
      </div>

      <Modal
        title="Add Chat to Home Screen"
        open={iosInstructionsVisible}
        onCancel={() => setIosInstructionsVisible(false)}
        footer={
          <Button type="primary" onClick={() => setIosInstructionsVisible(false)}>
            Got it
          </Button>
        }
      >
        <p>
          <strong>On iPhone / iPad:</strong> Tap the Share button (square with arrow) in Safari, then tap &quot;Add to Home Screen&quot;. Open the new icon to use Chat as an app.
        </p>
        <p>
          <strong>On Android:</strong> In Chrome, tap the menu (three dots) and choose &quot;Add to Home screen&quot; or &quot;Install app&quot;.
        </p>
      </Modal>
    </>
  );
};
